import { useCallback, useRef } from 'react';
import { Modal } from '@swtorbit/ui/react';
import { useTrlImageLibrary } from './TrlImageLibraryContext';
import { MetadataForm, type MetadataFormValues } from './MetadataForm';
import { ScreenshotCropStage, type ScreenshotCropStageHandle } from './ScreenshotCropStage';

interface AddScreenshotModalProps {
  isOpen: boolean;
  onClose: () => void;
  prefillFile?: File | null;
}

export function AddScreenshotModal({ isOpen, onClose, prefillFile }: AddScreenshotModalProps) {
  const { uploadImage } = useTrlImageLibrary();
  const cropStageRef = useRef<ScreenshotCropStageHandle>(null);

  const handleSubmit = useCallback(
    async (values: MetadataFormValues) => {
      const webp = await cropStageRef.current?.getCroppedFile();
      if (!webp) throw new Error('Please select an image file.');
      await uploadImage(
        {
          name: values.name,
          tags: values.tags,
          logAreaId: values.logAreaId,
          x: values.x,
          y: values.y,
          z: values.z,
          facing: values.facing,
          difficulty: values.difficulty,
          faction: values.faction,
        },
        webp,
      );
      onClose();
    },
    [uploadImage, onClose],
  );

  return (
    <Modal open={isOpen} onClose={onClose} size="full" bodyClassName="p-0" title="Add Screenshot">
        <div className="d-flex h-100">
          {/* 2/3 — upload / crop */}
          <ScreenshotCropStage
            ref={cropStageRef}
            prefillFile={prefillFile}
            enablePaste
            style={{ flex: '2 1 0' }}
          />

          {/* 1/3 — metadata form */}
          <div
            className="overflow-y-auto"
            style={{ flex: '1 1 0', padding: '20px', borderLeft: '1px solid var(--color-teal-dark)' }}
          >
            <MetadataForm
              mode="add"
              idPrefix="trl-add"
              submitLabel="Upload Screenshot"
              submitBusyLabel="Uploading…"
              onSubmit={handleSubmit}
            />
          </div>
        </div>
      </Modal>
  );
}
